import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { AnyAction } from "redux";
import { Spin, Collapse, Breadcrumb, Tooltip } from "antd";
import { toast } from "react-toastify";
import {
    fetchReimbursementById,
    syncReimbursementByRef,
    fetchDynamicConfig,
    downloadReimbursementForm,
} from "../../../core/data/redux/reimbursementSlice";
import { all_routes } from "../../router/all_routes";
import {
    DynamicFieldConfig,
    FieldType,
    isBoolean,
    isCheckboxType,
    getVisibleFields,
    enrichFieldsWithValues,
    resolveLineValue,
    parseMsDate,
} from "./components/dynamicFieldUtils";

type AppDispatch = ThunkDispatch<any, any, AnyAction>;
const { Panel } = Collapse;

// ── Static helpers and utilities ──────────────────────────────────────────────

const formatDateTime = (iso: string): string => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const hh = String(d.getHours()).padStart(2, "0");
        const min = String(d.getMinutes()).padStart(2, "0");
        return `${dd}-${mm}-${d.getFullYear()}, ${hh}:${min}`;
    } catch { return iso; }
};

const parseSapDate = (raw: string): string | null => {
    const m = String(raw ?? "").match(/\/Date\((\d+)\)\//);
    if (!m) return null;
    const d = new Date(parseInt(m[1], 10));
    return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
};

const parseSapTime = (raw: string): string | null => {
    const t = String(raw ?? "").padStart(6, "0");
    return t.length >= 4 ? `${t.slice(0, 2)}:${t.slice(2, 4)}` : null;
};

const mapStatusLabel = (code: string): string => {
    const s = (code ?? "").toString().toUpperCase().trim();
    const labels: Record<string, string> = {
        N: "New", T: "To Be Approved", P: "Partially Approved",
        A: "Approved", R: "Rejected", D: "Processing Complete",
        X: "Deleted", B: "To Be Processed", C: "Closed",
        K: "Cancelled", S: "Settled",
    };
    return labels[s] ?? (code || "Draft");
};

const getStatusConfig = (code: string) => {
    const s = (code ?? "").toUpperCase();
    const label = mapStatusLabel(code);
    if (["A", "APPROVED", "S", "SETTLED"].includes(s))
        return { color: "#16A34A", bg: "#f0fdf4", border: "#bbf7d0", icon: "ti ti-circle-check", label };
    if (["R", "REJECTED", "K", "CANCELLED", "X", "DELETED"].includes(s))
        return { color: "#DC2626", bg: "#fef2f2", border: "#fecaca", icon: "ti ti-circle-x", label };
    if (["T", "N", "NEW", "TO BE APPROVED"].includes(s))
        return { color: "#EA580C", bg: "#fff7ed", border: "#fed7aa", icon: "ti ti-clock", label };
    if (["P", "PARTIALLY APPROVED"].includes(s))
        return { color: "#F59E0B", bg: "#fffbeb", border: "#fde68a", icon: "ti ti-clock", label };
    if (["D", "PROCESSING COMPLETE", "C", "CLOSED", "B"].includes(s))
        return { color: "#7C3AED", bg: "#f5f3ff", border: "#ddd6fe", icon: "ti ti-clipboard-check", label };
    return { color: "#6C757D", bg: "#f8f9fa", border: "#dee2e6", icon: "ti ti-info-circle", label };
};

const triggerFileDownload = (fileName: string, base64: string, ext: string) => {
    try {
        const bytes = atob(base64);
        const arr = new Uint8Array(bytes.length);
        for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
        const mimes: Record<string, string> = {
            pdf: "application/pdf",
            xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png",
        };
        const blob = new Blob([arr], { type: mimes[ext?.toLowerCase()] ?? "application/octet-stream" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(a.href);
    } catch (e) {
        toast.error("Download failed.");
    }
};

// ── Component ─────────────────────────────────────────────────────────────────

const ReimbursementViewDetails = () => {
    const { id: referenceNumber } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const routes = all_routes;

    const { user } = useSelector((state: any) => state.authState);
    const empId: string = user?.data?.user?.empId ?? user?.empId ?? "";

    // ── State ──────────────────────────────────────────────────────────────
    const [isWaiting, setIsWaiting] = useState(false);
    const [reimbDetails, setReimbDetails] = useState<any>(null);

    // Config-derived state
    const [dynamicFields, setDynamicFields] = useState<DynamicFieldConfig[]>([]);
    const [multilineHeaders, setMultilineHeaders] = useState<DynamicFieldConfig[]>([]);
    const [termConditions, setTermConditions] = useState<any[]>([]);
    const [configDependants, setConfigDependants] = useState<any[]>([]);

    // ── Fetch config after knowing Reimbursement & Request type ───────────
    const fetchConfig = useCallback(async (remType: string, reqType: string, ltcCode: string) => {
        if (!remType) return;
        try {
            const action = await dispatch(
                fetchDynamicConfig({
                    ReimbursementType: remType,
                    RequestType: reqType,
                    LTCSchemeCode: ltcCode,
                    RequestOperation: "EV",     // EV = Employee View (read-only mode)
                    EmpID: empId,
                    ReferenceNumber: referenceNumber,
                })
            );
            if (!fetchDynamicConfig.fulfilled.match(action)) return;

            const payload: any = action.payload;
            const cfg = payload?.configData ?? payload;

            // ── DynamicFields ─────────────────────────────────────────────
            const dfRaw = cfg?.DynamicFields ?? cfg?.parsedDynamicFields;
            let fields: DynamicFieldConfig[] = [];
            if (Array.isArray(dfRaw)) {
                fields = dfRaw;
            } else if (typeof dfRaw === "string") {
                try {
                    const p = JSON.parse(dfRaw);
                    fields = Array.isArray(p) ? p : (p?.results ?? []);
                } catch { fields = []; }
            } else if (Array.isArray(cfg?.parsedDynamicFields)) {
                fields = cfg.parsedDynamicFields;
            }
            setDynamicFields(fields);

            // ── MultilineTableHeaders ─────────────────────────────────────
            const mlRaw = cfg?.MultilineTableHeaders ?? cfg?.parsedMultilineTableHeaders;
            let mlHeaders: DynamicFieldConfig[] = [];
            if (Array.isArray(mlRaw)) {
                mlHeaders = mlRaw;
            } else if (typeof mlRaw === "string") {
                try {
                    const p = JSON.parse(mlRaw);
                    mlHeaders = Array.isArray(p) ? p : (p?.results ?? []);
                } catch { mlHeaders = []; }
            }
            setMultilineHeaders(mlHeaders);

            // ── TermCondition ─────────────────────────────────────────────
            const tcRaw = cfg?.TermCondition ?? cfg?.parsedTermCondition;
            let terms: any[] = [];
            if (Array.isArray(tcRaw)) {
                terms = tcRaw;
            } else if (typeof tcRaw === "string") {
                try { terms = JSON.parse(tcRaw) ?? []; } catch { terms = []; }
            }
            setTermConditions(terms);

            // ── Config Dependants (BirthDate fallback) ────────────────────
            const deps = payload?.toDependants ?? cfg?.toDependants;
            setConfigDependants(Array.isArray(deps) ? deps : []);
        } catch (e) {
            console.error("fetchConfig error:", e);
        }
    }, [dispatch, empId, referenceNumber]);

    // ── Sync empty ToDynamics ─────────────────────────────────────────────
    const syncIfEmpty = useCallback(async (details: any) => {
        const td = details?.ToDynamics;
        const isEmpty = !td || (Array.isArray(td) ? td.length === 0 : Object.keys(td).length === 0);
        if (isEmpty && referenceNumber) {
            try { await dispatch(syncReimbursementByRef(referenceNumber)); }
            catch { /* silent – data may still display with DefaultValues */ }
        }
    }, [dispatch, referenceNumber]);

    // ── Primary fetch ─────────────────────────────────────────────────────
    const fetchDetails = useCallback(async () => {
        if (!referenceNumber) return;
        setIsWaiting(true);
        try {
            const action = await dispatch(fetchReimbursementById(referenceNumber));
            if (!fetchReimbursementById.fulfilled.match(action)) {
                toast.error("Failed to load reimbursement details.");
                return;
            }
            const data: any = action.payload;
            setReimbDetails(data);
            syncIfEmpty(data);
            fetchConfig(
                data?.Reimbursement_Type_ID ?? data?.ReimbursementType ?? "",
                data?.RequestType ?? "",
                data?.LTCSchemeCode ?? ""
            );
        } catch (e) {
            toast.error("An error occurred.");
        } finally {
            setIsWaiting(false);
        }
    }, [dispatch, referenceNumber, fetchConfig, syncIfEmpty]);

    useEffect(() => { fetchDetails(); }, []);   // eslint-disable-line

    // ── Print / Download ──────────────────────────────────────────────────
    const handlePrint = async () => {
        if (!referenceNumber) return;
        setIsWaiting(true);
        try {
            const action = await dispatch(downloadReimbursementForm({ empId, referenceNumber }));
            if (downloadReimbursementForm.fulfilled.match(action)) {
                const p: any = action.payload;
                const file = Array.isArray(p) ? p[0] : p;
                if (file?.Content) {
                    triggerFileDownload(
                        file?.FileName ?? `Reimbursement_${referenceNumber}.pdf`,
                        file.Content,
                        file?.FileExtension ?? "pdf"
                    );
                } else {
                    toast.warn("No file content received.");
                }
            } else {
                toast.error("Failed to download form.");
            }
        } finally {
            setIsWaiting(false);
        }
    };

    // ── Derived data ──────────────────────────────────────────────────────

    /** ToDynamics flat object (handles both array and object shapes) */
    const toDynamicsObj: Record<string, any> = (() => {
        const td = reimbDetails?.ToDynamics;
        if (!td) return {};
        if (Array.isArray(td)) return td[0] ?? {};
        return td;
    })();

    const userPayScale =
        reimbDetails?.PayScale ||
        reimbDetails?.pay_scale ||
        reimbDetails?.employeeDetails?.pay_scale ||
        user?.details?.pay_scale ||
        user?.data?.user?.details?.pay_scale ||
        "";

    const userSalary =
        reimbDetails?.BasicSalary ||
        reimbDetails?.basicSalary ||
        reimbDetails?.employeeDetails?.basicSalary ||
        user?.details?.basicSalary ||
        user?.data?.user?.details?.basicSalary ||
        "";

    /**
     * Visible dynamic fields enriched with their resolved display values.
     * Uses the centralised utility so display logic is consistent everywhere.
     */
    const enrichedFields = enrichFieldsWithValues(
        getVisibleFields(dynamicFields, "view"),
        toDynamicsObj,
        userPayScale,
        userSalary
    );

    /** Filter ToMultiLines into a proper array */
    const multiLines: any[] = (() => {
        const ml = reimbDetails?.ToMultiLines;
        if (!ml) return [];
        if (Array.isArray(ml)) return ml;
        if (Array.isArray(ml?.results)) return ml.results;
        return [];
    })();

    /** MultilineHeaders filtered for visibility */
    const visibleLineHeaders = multilineHeaders.filter((h) => isBoolean(h.VisibleToEmployee));

    /**
     * ALL dependants from the request (mobile shows all, not just selected).
     * DependentSelected flag is preserved so we can render a checkbox per row.
     */
    const allDependants: any[] = (() => {
        const td = reimbDetails?.ToDependants;
        if (!td) return [];
        if (Array.isArray(td)) return td;
        if (Array.isArray(td?.results)) return td.results;
        return [];
    })();
    // Keep for any internal use that needs only the checked subset
    const selectedDependants: any[] = allDependants.filter((d: any) => d?.DependentSelected);

    /** Filtered remarks (exclude system/ABAP entries) */
    const filteredRemarks: any[] = (reimbDetails?.remarks_data ?? []).filter(
        (r: any) =>
            r?.commentBy !== "S" &&
            r?.approverId !== "00000000" &&
            r?.approverId !== "ABAP2" &&
            r?.employeeName !== ""
    );

    /** Terms joined into readable paragraphs */
    const termParagraphs = (() => {
        const paras: string[] = [];
        let cur = "";
        termConditions.forEach((tc: any) => {
            const line = (tc.TextLine ?? "").trim();
            if (line === "") {
                if (cur.trim()) { paras.push(cur.trim()); cur = ""; }
            } else {
                cur += (cur ? " " : "") + line;
            }
        });
        if (cur.trim()) paras.push(cur.trim());
        return paras.filter(Boolean);
    })();

    /** Requested / approved value (use Number, fallback to Quantity) */
    const requestedValue = reimbDetails?.RequestedAmount === "0.00"
        ? (reimbDetails?.RequestedNumber ?? "-")
        : (reimbDetails?.RequestedAmount ?? "-");
    const approvedValue = reimbDetails?.ApprovedAmount === "0.00"
        ? (reimbDetails?.ApprovedNumber ?? "-")
        : (reimbDetails?.ApprovedAmount ?? "-");

    const isLTC = reimbDetails?.ReimbursementType === "ZLTA" || reimbDetails?.Reimbursement_Type_ID === "ZLTA";
    const statusCfg = getStatusConfig(reimbDetails?.RequestStatus ?? "");

    // ── Guard renders ─────────────────────────────────────────────────────
    if (isWaiting && !reimbDetails) {
        return (
            <div className="page-wrapper d-flex justify-content-center align-items-center" style={{ minHeight: "80vh" }}>
                <Spin size="large" tip="Loading details…" />
            </div>
        );
    }

    if (!reimbDetails && !isWaiting) {
        return (
            <div className="page-wrapper d-flex flex-column justify-content-center align-items-center" style={{ minHeight: "80vh" }}>
                <i className="ti ti-file-off fs-1 text-muted opacity-25 mb-3" />
                <h6 className="text-muted mb-3">Record not found</h6>
                <button className="btn btn-sm btn-outline-secondary px-4 rounded-2" onClick={() => navigate(-1)}>← Back</button>
            </div>
        );
    }

    // ── Render ────────────────────────────────────────────────────────────
    return (
        <div className="page-wrapper" style={{ backgroundColor: "#f4f5f7" }}>
            {/* Overlay spinner for background ops */}
            {isWaiting && reimbDetails && (
                <div style={{ position: "fixed", inset: 0, background: "rgba(255,255,255,0.6)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Spin size="large" />
                </div>
            )}

            <div className="content">
                {/* ── Header ─────────────────────────────────────────────── */}
                <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
                    <div>
                        <h5 className="fw-bold text-dark mb-0">Reimbursement Details</h5>
                        <Breadcrumb
                            style={{ fontSize: 11, marginTop: 4 }}
                            items={[
                                { title: <Link to={routes.adminDashboard} className="text-muted">Home</Link> },
                                { title: <Link to={`/${routes.Reimbursement?.list ?? "reimbursement-list"}`} className="text-muted">Reimbursement</Link> },
                                { title: <span style={{ color: "#ab1f24" }}>View Details</span> },
                            ]}
                        />
                    </div>
                    <div className="d-flex gap-2">
                        <Tooltip title="Download / Print Form">
                            <button
                                id="btn-print-reimb-form"
                                className="btn btn-outline-secondary btn-sm px-3 rounded-2"
                                onClick={handlePrint}
                                disabled={isWaiting}
                            >
                                <i className="ti ti-printer me-1" />Print Form
                            </button>
                        </Tooltip>
                        <button
                            className="btn btn-outline-secondary btn-sm px-3 rounded-2"
                            onClick={() => navigate(-1)}
                        >
                            <i className="ti ti-arrow-left me-1" />Back
                        </button>
                    </div>
                </div>

                {/* ── Status Banner ────────────────────────────────────── */}
                <div className="card border-0 rounded-2 mb-4"
                    style={{ background: statusCfg.bg, border: `1px solid ${statusCfg.border}`, borderLeft: `4px solid ${statusCfg.color}` }}>
                    <div className="card-body py-3 px-4 d-flex align-items-center gap-3">
                        <i className={`${statusCfg.icon}`} style={{ color: statusCfg.color, fontSize: '32px' }} />
                        <div>
                            <div className="text-muted fw-bold mb-0" style={{ fontSize: 10, letterSpacing: 1 }}>REQUEST STATUS</div>
                            <h5 className="fw-bold mb-0" style={{ color: statusCfg.color }}>{statusCfg.label}</h5>
                        </div>
                    </div>
                </div>

                {/* ── Application Info ────────────────────────────────── */}
                <div className="card border shadow-sm rounded-2 mb-4">
                    <div className="card-header bg-light border-bottom py-3 px-4 d-flex align-items-center gap-2">
                        <i className="ti ti-plane text-dark" style={{ fontSize: '22px' }} />
                        <h5 className="fw-bold text-dark mb-0" style={{ fontSize: '18px' }}>Application Info</h5>
                    </div>
                    <div className="card-body px-4 py-4">
                        <div className="row g-4">
                            <InfoRow label="Employee ID" value={reimbDetails?.EmpID} />
                            <InfoRow label="Approver User ID" value={reimbDetails?.ApproverUserID} />
                            <InfoRow label="Reference No." value={reimbDetails?.ReferenceNumber} highlight />
                            {reimbDetails?.ReferenceNumber1 && (
                                <InfoRow label="Assoc. Ref. No." value={reimbDetails.ReferenceNumber1} />
                            )}
                            <InfoRow label="Reimbursement Type" value={reimbDetails?.Reimbursement_Type ?? reimbDetails?.ReimbursementType} />
                            <InfoRow label="Request Type" value={reimbDetails?.Request_Type ?? reimbDetails?.RequestType} />
                            {isLTC && (
                                <>
                                    <InfoRow label="Scheme" value={reimbDetails?.LTCSchemeCode === "AI" ? "All India" : "Home Town"} />
                                    <InfoRow label="Block Begin Date" value={parseMsDate(reimbDetails?.LTCBlockBeginDate) || reimbDetails?.LTCBlockBeginDate} />
                                    <InfoRow label="Block End Date" value={parseMsDate(reimbDetails?.LTCBlockEndDate) || reimbDetails?.LTCBlockEndDate} />
                                </>
                            )}
                            <InfoRow label="Requested Value" value={requestedValue} />
                            <InfoRow label="Approved Value" value={approvedValue} />
                        </div>
                    </div>
                </div>

                {/* ── Collapsible sections ──────────────────────────── */}
                <Collapse
                    className="reimb-details-collapse mb-3"
                    bordered={false}
                    expandIconPosition="end"
                    style={{ background: "transparent", display: "flex", flexDirection: "column", gap: 12 }}
                >

                    {/* ── Dynamic Details ────────────────────────────── */}
                    {enrichedFields.length > 0 && (
                        <Panel key="details" header={<PanelHdr icon="ti ti-users" title="Details" />}
                            className="reimb-panel shadow-sm rounded-2">
                            <DynamicFieldsReadOnly fields={enrichedFields} />
                        </Panel>
                    )}

                    {/* ── Line Items ─────────────────────────────────── */}
                    {multiLines.length > 0 && (
                        <Panel key="lines" header={<PanelHdr icon="ti ti-list-details" title="Line Items" />}
                            className="reimb-panel shadow-sm rounded-2">
                            {multiLines.map((row: any, ri: number) => (
                                <div key={ri} className={ri < multiLines.length - 1 ? "pb-3 mb-3 border-bottom" : "pb-1"}>
                                    <div className="fw-bold small mb-2" style={{ color: "#ab1f24" }}>Item {ri + 1}</div>
                                    <div className="row g-4">
                                        {visibleLineHeaders.map((col, ci) => (
                                            <InfoRow
                                                key={ci}
                                                label={col.Label ?? col.LineCustomerField ?? ""}
                                                value={resolveLineValue(col, row)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </Panel>
                    )}

                    {/* ── Dependants ─────────────────────────────────── */}
                    {allDependants.length > 0 && (
                        <Panel key="deps" header={<PanelHdr icon="ti ti-users" title={`Dependants (${allDependants.length})`} />}
                            className="reimb-panel shadow-sm rounded-2">
                            {allDependants.map((dep: any, di: number) => {
                                const cfgDep = configDependants.find((c: any) => c?.FamsaObjps === dep?.FamsaObjps);
                                const birthDate = (dep?.BirthDate && dep.BirthDate !== "--")
                                    ? dep.BirthDate
                                    : (cfgDep?.BirthDate ?? "");
                                return (
                                    <div key={di} className={di < allDependants.length - 1 ? "pb-3 mb-3 border-bottom" : "pb-1"}>
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <span className="fw-bold small" style={{ color: "#ab1f24" }}>
                                                {dep?.FullName ?? dep?.FirstName ?? `Dependant ${di + 1}`}
                                            </span>
                                            {/* Checkbox icon matching mobile DependentSelected indicator */}
                                            <i
                                                className={dep?.DependentSelected
                                                    ? "ti ti-square-check-filled text-success"
                                                    : "ti ti-square text-muted opacity-50"}
                                                style={{ fontSize: "26px", lineHeight: 1 }}
                                                title={dep?.DependentSelected ? "Selected" : "Not selected"}
                                            />
                                        </div>
                                        <div className="row g-4">
                                            <InfoRow label="Relation" value={dep?.Name} />
                                            {dep?.Gender && <InfoRow label="Gender" value={dep.Gender === "M" ? "Male" : dep.Gender === "F" ? "Female" : dep.Gender} />}
                                            {birthDate && birthDate !== "--" && <InfoRow label="Birth Date" value={birthDate} />}
                                            {dep?.AgeYears && <InfoRow label="Age" value={`${parseInt(dep.AgeYears, 10)} yrs`} />}
                                        </div>
                                    </div>
                                );
                            })}
                        </Panel>
                    )}

                    {/* ── Previous Remarks ───────────────────────────── */}
                    <Panel key="remarks" header={<PanelHdr icon="ti ti-users" title="Previous Remarks" />}
                        className="reimb-panel shadow-sm rounded-2">
                        {filteredRemarks.length === 0 ? (
                            <EmptyMsg text="No remarks available." />
                        ) : (
                            filteredRemarks.map((r: any, ri: number) => {
                                const dateStr = parseSapDate(r?.createdDate ?? "") ?? (r?.createdAt ? formatDateTime(r.createdAt) : "");
                                const timeStr = parseSapTime(r?.createdTime ?? "");
                                const dt = dateStr && timeStr ? `${dateStr}, ${timeStr}` : dateStr;
                                return (
                                    <RemarkRow
                                        key={ri}
                                        name={r?.employeeName}
                                        userId={r?.approverId}
                                        text={r?.remarkText}
                                        dateTime={dt}
                                        isLast={ri === filteredRemarks.length - 1}
                                    />
                                );
                            })
                        )}
                    </Panel>

                    {/* ── Attachments ────────────────────────────────── */}
                    {(reimbDetails?.attachments_data?.length ?? 0) > 0 && (
                        <Panel key="attach" header={<PanelHdr icon="ti ti-paperclip" title="Attachments" />}
                            className="reimb-panel shadow-sm rounded-2">
                            <div className="d-flex flex-column gap-2">
                                {reimbDetails.attachments_data.map((doc: any, ai: number) => (
                                    <AttachItem key={doc.id ?? ai} doc={doc} />
                                ))}
                            </div>
                        </Panel>
                    )}

                    {/* ── Terms & Conditions ─────────────────────────── */}
                    {termParagraphs.length > 0 && (
                        <Panel key="terms" header={<PanelHdr icon="ti ti-file-description" title="Terms & Conditions" />}
                            className="reimb-panel shadow-sm rounded-2">
                            <ul className="mb-0 ps-3" style={{ lineHeight: 1.7 }}>
                                {termParagraphs.map((p, pi) => (
                                    <li key={pi} className="text-muted small">{p}</li>
                                ))}
                            </ul>
                        </Panel>
                    )}

                </Collapse>
                <div style={{ height: 40 }} />
            </div>

            {/* ── Styles ──────────────────────────────────────────────── */}
            <style>{`
                .reimb-details-collapse {
                    background: transparent;
                }
                .reimb-details-collapse .ant-collapse-item {
                    background: #fff;
                    border: 1px solid #dee2e6 !important;
                    border-radius: 6px !important;
                    margin-bottom: 12px;
                    overflow: hidden;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                }
                .reimb-details-collapse .ant-collapse-header {
                    padding: 12px 20px !important;
                    background: #f8f9fa;
                    font-weight: 600;
                    color: #343a40 !important;
                    align-items: center;
                    border-radius: 6px 6px 0 0 !important;
                }
                .reimb-details-collapse .ant-collapse-content { 
                    border-top: 1px solid #dee2e6; 
                }
                .reimb-details-collapse .ant-collapse-content-box { 
                    padding: 20px !important; 
                }
                .reimb-panel { border: none !important; }
                
                .info-row-lbl { font-size: 13px; color: #6c757d; font-weight: 500; }
                .info-row-val { font-size: 14px; color: #212529; font-weight: 500; }
                .info-row-val.hl { color: #ab1f24; font-weight: 600; }
                
                .remark-line { position: absolute; left: 19px; top: 0; bottom: 0; width: 2px; background: #dee2e6; z-index: 0; }
                .remark-avatar { 
                    width: 40px; height: 40px; border-radius: 6px; background: #6c757d; color: #fff;
                    display: flex; align-items: center; justify-content: center; font-weight: 600; font-size: 16px;
                    flex-shrink: 0; position: relative; z-index: 1; 
                }
            `}</style>
        </div>
    );
};

// ── Dynamic fields read-only renderer ────────────────────────────────────────

const DynamicFieldsReadOnly: React.FC<{
    fields: Array<DynamicFieldConfig & { resolvedValue: string; rawValue: any }>;
}> = ({ fields }) => {
    if (!fields.length) return <EmptyMsg text="No details configured for this claim type." />;

    // Separate checkboxes from regular rows
    const checkboxes = fields.filter((f) => isCheckboxType(f.ViewOfFieldType as FieldType));
    const regular = fields.filter((f) => !isCheckboxType(f.ViewOfFieldType as FieldType));

    return (
        <>
            {/* Regular label-value pairs */}
            {regular.length > 0 && (
                <div className="row g-4">
                    {regular.map((f, i) => (
                        <InfoRow
                            key={i}
                            label={(f.Label || "").endsWith("*") ? (f.Label || "").slice(0, -1).trim() : (f.Label || "")}
                            value={f.resolvedValue}
                            mandatory={isBoolean(f.CustFldMandatory)}
                            readOnly={isBoolean(f.ReadOnlyForEmp)}
                        />
                    ))}
                </div>
            )}

            {/* Checkbox fields shown as badge list */}
            {checkboxes.length > 0 && (
                <div className="mt-3 pt-3 border-top">
                    <div className="fw-semibold small text-muted text-uppercase mb-2" style={{ letterSpacing: 1 }}>
                        Declarations
                    </div>
                    <div className="d-flex flex-column gap-2">
                        {checkboxes.map((f, i) => {
                            const checked = f.rawValue === "YES" || f.rawValue === true ||
                                String(f.rawValue ?? "").toLowerCase() === "true";
                            return (
                                <div key={i} className="d-flex align-items-center gap-2">
                                    <span
                                        className="rounded-2 d-flex align-items-center justify-content-center flex-shrink-0"
                                        style={{
                                            width: 22, height: 22,
                                            background: checked ? "#dcfce7" : "#fef2f2",
                                            border: `1.5px solid ${checked ? "#16a34a" : "#dc2626"}`,
                                        }}
                                    >
                                        <i className={`ti ${checked ? "ti-check" : "ti-x"} fw-bold`}
                                            style={{ fontSize: 11, color: checked ? "#16a34a" : "#dc2626" }} />
                                    </span>
                                    <span className="small text-dark fw-medium">
                                        {(f.Label || "").endsWith("*") ? (f.Label || "").slice(0, -1).trim() : (f.Label || "")}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </>
    );
};

// ── Sub-components ────────────────────────────────────────────────────────────

const InfoRow: React.FC<{
    label: string;
    value?: any;
    highlight?: boolean;
    mandatory?: boolean;
    readOnly?: boolean;
}> = ({ label, value, highlight, mandatory, readOnly }) => (
    <div className="col-12 col-sm-6 col-md-4 col-lg-3 px-3 d-flex flex-column gap-1 mb-3">
        <span className="info-row-lbl">
            {label}
            {mandatory && <span className="text-danger ms-1">*</span>}
            {readOnly && (
                <Tooltip title="Pre-filled by SAP">
                    <i className="ti ti-lock text-muted ms-1" style={{ fontSize: 10 }} />
                </Tooltip>
            )}
        </span>
        <span className={`info-row-val${highlight ? " hl" : ""}`}>{value ?? "-"}</span>
    </div>
);

const PanelHdr: React.FC<{ icon: string; title: string }> = ({ icon, title }) => (
    <div className="d-flex align-items-center gap-2">
        <i className={`${icon} text-muted`} style={{ fontSize: '20px' }} />
        <span className="fw-semibold text-dark" style={{ fontSize: '16px' }}>{title}</span>
    </div>
);

const EmptyMsg: React.FC<{ text: string }> = ({ text }) => (
    <div className="text-center py-3 text-muted small">
        <i className="ti ti-info-circle me-1" />{text}
    </div>
);

const RemarkRow: React.FC<{
    name: string; userId: string; text: string; dateTime: string; isLast: boolean;
}> = ({ name, userId, text, dateTime, isLast }) => (
    <div className="d-flex gap-3 mb-3" style={{ position: "relative" }}>
        {!isLast && <div className="remark-line" style={{ top: 38 }} />}
        <div className="remark-avatar">{(name ?? userId ?? "?")[0]?.toUpperCase()}</div>
        <div className="flex-grow-1 rounded-2 p-3" style={{ background: "#f8f9fa", border: "1px solid #e9ecef" }}>
            <div className="d-flex justify-content-between flex-wrap gap-1 mb-1">
                <span className="fw-bold text-dark small">
                    {name || "-"}
                    {userId && <span className="text-muted ms-2" style={{ fontSize: 11 }}>({userId})</span>}
                </span>
                {dateTime && (
                    <span className="text-muted" style={{ fontSize: 11 }}>
                        <i className="ti ti-clock me-1" />{dateTime}
                    </span>
                )}
            </div>
            <p className="mb-0 text-secondary small" style={{ fontStyle: "italic", lineHeight: 1.5 }}>{text || "—"}</p>
        </div>
    </div>
);

const AttachItem: React.FC<{ doc: any }> = ({ doc }) => {
    const fileName = doc?.FileName ?? doc?.fileName ?? doc?.name ?? "Attachment";
    const fileExt = (doc?.FileExtension ?? doc?.fileExtension ?? (fileName.includes('.') ? fileName.split('.').pop() : "") ?? "").toLowerCase();
    const ext = fileExt.replace(/^\./, "");

    const icon =
        ext === "pdf" ? "ti ti-file-type-pdf text-danger" :
            ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext) ? "ti ti-photo text-primary" :
                ["doc", "docx"].includes(ext) ? "ti ti-file-type-doc text-primary" :
                    ["xls", "xlsx", "csv"].includes(ext) ? "ti ti-file-type-xls text-success" :
                        "ti ti-file text-muted";

    const handleClick = () => {
        const fileUrl = doc?.attachmentUrl || doc?.AttachmentUrl || doc?.attachment_url || doc?.fileUrl || doc?.file_url || doc?.url || doc?.serverURL || doc?.s3Url || doc?.downloadUrl || doc?.signedUrl;
        const content = doc?.Content || doc?.content || doc?.FileContent || doc?.fileContent || doc?.file || "";

        if (fileUrl) {
            window.open(fileUrl, "_blank", "noopener,noreferrer");
        } else if (content) {
            triggerFileDownload(fileName, content, ext || "bin");
        } else {
            toast.warn("No file content available.");
        }
    };

    return (
        <div className="d-flex align-items-center gap-3 p-3 rounded-2 border" role="button" tabIndex={0}
            style={{ background: "#f8f9fa", cursor: "pointer" }}
            onClick={handleClick} onKeyDown={(e) => e.key === "Enter" && handleClick()}>
            <i className={`${icon}`} style={{ fontSize: '24px' }} />
            <div className="flex-grow-1 overflow-hidden">
                <div className="fw-semibold small text-dark text-truncate">{fileName}</div>
                {ext && <div className="text-muted" style={{ fontSize: 11 }}>{ext.toUpperCase()}</div>}
            </div>
            <i className="ti ti-external-link text-muted" style={{ fontSize: '20px' }} />
        </div>
    );
};

export default ReimbursementViewDetails;
